/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * BottomNavStylePicker
 *
 * Three clickable thumbnail cards for mobile bottom nav sub-item display style.
 * Reads from layoutStore, calls setBottomNavStyle on click.
 * Only visible on mobile (<=768px) or shown always in settings for configuration.
 */

import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { useLayoutStore } from '@/stores/layoutStore';
import type { BottomNavStyle } from '@/components/layouts/mobile-bottom-nav/types';

const OPTIONS: { value: BottomNavStyle; previewId: string }[] = [
  { value: 'horizontal', previewId: 'horizontal' },
  { value: 'vertical', previewId: 'vertical' },
  { value: 'grid', previewId: 'grid' },
];

export function BottomNavStylePicker() {
  const { t } = useTranslation('common');
  const { bottomNavStyle, setBottomNavStyle } = useLayoutStore();

  return (
    <div className="bnsp-container">
      <div className="bnsp-header">
        <h3 className="bnsp-title">
          {t('bottomNavSettings.title', 'Mobile Navigation Style')}
        </h3>
        <p className="bnsp-description">
          {t('bottomNavSettings.description', 'Choose how sub-items appear when you tap a section in the bottom navigation bar.')}
        </p>
      </div>

      <div className="bnsp-grid">
        {OPTIONS.map(({ value, previewId }) => {
          const isSelected = bottomNavStyle === value;
          return (
            <button
              key={value}
              className={`bnsp-card ${isSelected ? 'selected' : ''}`}
              onClick={() => setBottomNavStyle(value)}
              type="button"
            >
              <div className="bnsp-preview">
                <NavPreview type={previewId} />
              </div>
              <div className="bnsp-label">
                <span className="bnsp-name">
                  {t(`bottomNavSettings.${value}.name`, value.charAt(0).toUpperCase() + value.slice(1))}
                </span>
                <span className="bnsp-desc">
                  {t(`bottomNavSettings.${value}.description`, '')}
                </span>
              </div>
              {isSelected && (
                <div className="bnsp-check">
                  <Check size={14} />
                </div>
              )}
            </button>
          );
        })}
      </div>

      <style>{styles}</style>
    </div>
  );
}

function NavPreview({ type }: { type: string }) {
  if (type === 'vertical') {
    return (
      <div className="bnsp-mini bnsp-mini-vertical">
        <div className="bnsp-mini-panel">
          <div className="bnsp-mini-row" />
          <div className="bnsp-mini-row short" />
          <div className="bnsp-mini-row" />
        </div>
        <div className="bnsp-mini-bar">
          <div className="bnsp-mini-dot active" />
          <div className="bnsp-mini-dot" />
          <div className="bnsp-mini-dot" />
        </div>
      </div>
    );
  }

  if (type === 'grid') {
    return (
      <div className="bnsp-mini bnsp-mini-grid">
        <div className="bnsp-mini-grid-panel">
          <div className="bnsp-mini-cell" />
          <div className="bnsp-mini-cell" />
          <div className="bnsp-mini-cell" />
          <div className="bnsp-mini-cell" />
          <div className="bnsp-mini-cell" />
          <div className="bnsp-mini-cell" />
        </div>
        <div className="bnsp-mini-bar">
          <div className="bnsp-mini-dot active" />
          <div className="bnsp-mini-dot" />
          <div className="bnsp-mini-dot" />
        </div>
      </div>
    );
  }

  // horizontal (default)
  return (
    <div className="bnsp-mini bnsp-mini-horizontal">
      <div className="bnsp-mini-content" />
      <div className="bnsp-mini-hbar">
        <div className="bnsp-mini-dot active" />
        <div className="bnsp-mini-chip" />
        <div className="bnsp-mini-chip" />
        <div className="bnsp-mini-chip short" />
      </div>
    </div>
  );
}

const styles = `
  .bnsp-container {
    margin-bottom: 24px;
  }

  .bnsp-header {
    margin-bottom: 16px;
  }

  .bnsp-title {
    font-size: 14px;
    font-weight: 600;
    color: var(--color-text-primary);
    margin: 0 0 4px;
  }

  .bnsp-description {
    font-size: 12px;
    color: var(--color-text-muted);
    margin: 0;
  }

  .bnsp-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
  }

  @media (max-width: 640px) {
    .bnsp-grid {
      grid-template-columns: 1fr;
    }
  }

  .bnsp-card {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 12px;
    background: var(--color-bg-primary);
    border: 2px solid var(--color-border-subtle);
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.15s ease;
    text-align: left;
  }

  .bnsp-card:hover {
    border-color: var(--color-border-default);
    background: var(--color-bg-hover);
  }

  .bnsp-card.selected {
    border-color: var(--color-brand);
    box-shadow: 0 0 0 1px var(--color-brand);
  }

  .bnsp-check {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: var(--color-brand);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .bnsp-label {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .bnsp-name {
    font-size: 12px;
    font-weight: 600;
    color: var(--color-text-primary);
  }

  .bnsp-desc {
    font-size: 10px;
    color: var(--color-text-muted);
    line-height: 1.3;
  }

  .bnsp-preview {
    height: 64px;
    border-radius: 6px;
    overflow: hidden;
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
  }

  .bnsp-mini {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  /* Shared mini elements */
  .bnsp-mini-bar {
    display: flex;
    align-items: center;
    justify-content: space-around;
    padding: 4px 8px;
    border-top: 1px solid var(--color-border-subtle);
    background: var(--color-bg-secondary);
  }

  .bnsp-mini-dot {
    width: 8px;
    height: 8px;
    border-radius: 2px;
    background: var(--color-border-default);
  }

  .bnsp-mini-dot.active {
    background: var(--color-brand);
  }

  .bnsp-mini-content {
    flex: 1;
    background: var(--color-bg-primary);
  }

  /* Horizontal preview */
  .bnsp-mini-horizontal .bnsp-mini-content {
    flex: 1;
  }

  .bnsp-mini-hbar {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 4px 6px;
    border-top: 1px solid var(--color-border-subtle);
    background: var(--color-bg-secondary);
  }

  .bnsp-mini-chip {
    height: 6px;
    width: 20px;
    border-radius: 3px;
    background: var(--color-border-default);
  }

  .bnsp-mini-chip.short {
    width: 14px;
  }

  /* Vertical preview */
  .bnsp-mini-vertical {
    justify-content: flex-end;
  }

  .bnsp-mini-panel {
    padding: 4px 8px;
    border-bottom: 1px solid var(--color-border-subtle);
    background: var(--color-bg-secondary);
  }

  .bnsp-mini-row {
    height: 4px;
    border-radius: 2px;
    background: var(--color-border-default);
    margin-bottom: 3px;
    width: 70%;
  }

  .bnsp-mini-row.short {
    width: 50%;
  }

  /* Grid preview */
  .bnsp-mini-grid {
    justify-content: flex-end;
  }

  .bnsp-mini-grid-panel {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 3px;
    padding: 4px 6px;
    border-bottom: 1px solid var(--color-border-subtle);
    background: var(--color-bg-secondary);
  }

  .bnsp-mini-cell {
    height: 10px;
    border-radius: 2px;
    background: var(--color-border-default);
  }
`;
